/**
 * Constant representing the ID for non-union order templates.
 * @const {number}
 */
export const NON_UNION_ORDER_TEMPLATE_ID = 1;

/**
 * Constant representing the ID for the US SAG-AFTRA order template.
 * @const {number}
 */
export const AFTRA_ORDER_TEMPLATE_ID = 2;

/**
 * Constant representing the ID for the Canadian ACTRA order template.
 * @const {number}
 */
export const ACTRA_ORDER_TEMPLATE_ID = 3;

/**
 * Constant representing the list of filter parameters to exclude from counter calculations.
 *
 * This constant holds an array of parameter names that should not be considered when calculating
 * the count of items based on applied filters.
 * @note The `agentIds` parameter can be excluded as it's not represented in the filter fields directly.
 *
 * @const {Array<string>}
 */
export const FILTERS_TO_EXCLUDE_FROM_COUNT: Array<string> = [
	'type',
	'sort',
	'page',
	'checkPending',
	'preview',
	'talent',
	'agentIds',
	'payerFirstName',
	'payerMiddleName'
];

/**
 * Constant representing the name of the gateway fee line item.
 * @const {string}
 */
export const OLIT_GATEWAY_NAME = 'Gateway fee';

/**
 * Constant representing the suffix to be added to the vendor name when creating an order.
 * @const {string}
 */
export const ORDER_VENDOR_SUFFIX = 'Vendor';

/**
 * Constant representing the name of the queue with orders that require action.
 * @const {string}
 */
export const ACTION_REQUIRED_QUEUE_NAME = 'Action Required';

/**
 * Constant representing the name of the vendor line items array filled in with the control form groups.
 * @const {string}
 */
export const ORDER_VENDOR_LINE_ITEMS_CONTROL_NAME = 'vendorLineItems';

/**
 * Enum representing the possible statuses associated with an order.
 * @enum {string}
 */
export enum OrderStatusType {
	email = 'email',
	payment = 'payment',
	collection = 'collection',
	quickbooks = 'quickbooks',
	queue = 'queue'
}

/**
 * Enum representing the possible actions associated with a bulk action on an order.
 * @enum {string}
 */
export enum OrderBulkAction {
	DownloadPayment = 'DownloadPayment',
	ApprovePayment = 'ApprovePayment',
	DeclinePayment = 'DeclinePayment',
	PushToQuickbooks = 'PushToQuickbooks',
	Cancel = 'Cancel',
	ReSendEmails = 'ReSendEmails',
	ResetQBO = 'ResetQBO',
	ResetPayment = 'ResetPayment',
	ResetCollection = 'ResetCollection'
}

/**
 * Enum representing the order line item template types.
 * @enum {string}
 */
export enum LineItemTemplateType {
	rate = 'rate',
	amount = 'amount',
	total = 'total'
}

/**
 * Enum representing the order queue types.
 * @enum {string}
 */
export enum OrderQueueType {
	normal = 'normal',
	deferred = 'deferred'
}

/**
 * Enum representing the order template types.
 * @enum {string}
 */
export enum OrderTemplateType {
	union = 'union',
	vendor = 'vendor'
}

/**
 * Enum representing the order calculation types.
 * @enum {string}
 */
export enum OrderCalculationType {
	rate = 'rate',
	lumpsum = 'lumpsum'
}
